import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { loginRequest } from '../../../../config/msalConfig';
import { env } from '../../../../env';
import { msalInstance } from '../../../../msalInstance';
import { useCompanyStore } from '../../../../store/company/companyStore';
import { useUserAuthStore } from '../../../../store/userAuth/userAuthStore';
import { AuthContextInterface } from '../../context/AuthContextInterface';
import { AuthUser } from '../../types';

const noopTokens = async () => ({ accessToken: null, refreshToken: null });

export const AzureAuthContext = createContext<AuthContextInterface>({
  user: null,
  loading: true,
  error: null,
  isAuthenticated: false,
  getTokens: noopTokens,
  loginWithEmailAndPassword: async () => undefined,
  loginWithCustomToken: async () => undefined,
  loginWithGoogle: async () => undefined,
  register: async () => undefined,
  logout: async () => undefined,
  refreshToken: async () => undefined,
  verifyEmail: async () => undefined,
  handleVerifyEmail: async () => ({ valid: true }),
  resetPassword: async () => undefined,
  setNewPassword: async () => undefined,
  emailVerified: true,
  authHook: undefined,
});

const mapAccountToAuthUser = (
  account?: AccountInfo | null,
): AuthUser | null => {
  if (!account) {
    return null;
  }

  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const oid = (claims?.oid as string | undefined) ?? account.localAccountId;
  const displayName =
    (claims?.name as string | undefined) ??
    (account.name as string | undefined) ??
    account.username;

  return {
    uid: oid ?? account.localAccountId,
    email: account.username,
    displayName,
  };
};

const unsupportedAction = (message: string) => {
  toast.info(message);
};

export const AzureAuthProvider = ({
  children,
}: React.PropsWithChildren<Record<string, unknown>>) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const syncUserState = useCallback((account?: AccountInfo | null) => {
    const mappedUser = mapAccountToAuthUser(account);
    setUser(mappedUser);
    setIsAuthenticated(!!mappedUser);

    if (mappedUser) {
      useUserAuthStore.getState().setUserState({
        user: mappedUser,
        userStatus: 'authenticated',
        accessToken: useUserAuthStore.getState().accessToken,
        refreshToken: null,
        error: null,
        dbUser: useUserAuthStore.getState().dbUser,
      });
    } else {
      useUserAuthStore.getState().resetUserState();
    }
  }, []);

  useEffect(() => {
    const accounts = msalInstance.getAllAccounts();
    syncUserState(accounts[0] ?? null);
    setLoading(false);

    const callbackId = msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        syncUserState(payload.account);
      }

      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        syncUserState(null);
        useCompanyStore.getState().resetCompanyState();
      }

      if (event.eventType === EventType.LOGIN_FAILURE) {
        setError(event.error);
        setLoading(false);
      }
    });

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, [syncUserState]);

  const acquireAccessToken = useCallback(async () => {
    try {
      const result = await msalInstance.acquireTokenSilent(loginRequest);
      const tokens = {
        accessToken: result.accessToken,
        refreshToken: null,
      };
      useUserAuthStore.getState().setAccessToken(tokens.accessToken);
      return tokens;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const interactiveResult =
          await msalInstance.acquireTokenPopup(loginRequest);
        const tokens = {
          accessToken: interactiveResult.accessToken,
          refreshToken: null,
        };
        useUserAuthStore.getState().setAccessToken(tokens.accessToken);
        return tokens;
      }
      setError(err);
      throw err;
    }
  }, []);

  const login = useCallback(async () => {
    setLoading(true);
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (err) {
      toast.error('Unable to start Microsoft sign-in flow');
      setError(err);
      setLoading(false);
    }
  }, []);

  const logout = useCallback(
    async (redirect = true) => {
      try {
        syncUserState(null);
        useCompanyStore.getState().resetCompanyState();

        await msalInstance.logoutRedirect({
          postLogoutRedirectUri: redirect
            ? `${env.VITE_APP_BASE_URL}/auth/login`
            : undefined,
        });
      } catch (err) {
        toast.error('Unable to sign out at the moment');
        setError(err);
      }
    },
    [syncUserState],
  );

  const handleVerifyEmail = useCallback(async () => {
    unsupportedAction('Email verification is handled via Microsoft Entra.');
    return { valid: true };
  }, []);

  const resetPassword = useCallback(async () => {
    unsupportedAction(
      'Password resets are handled on the Microsoft login page. Choose "Forgot password" during sign in.',
    );
  }, []);

  const setNewPassword = useCallback(async () => {
    unsupportedAction(
      'Password updates are handled directly in Microsoft Entra. Please use the Microsoft portal.',
    );
  }, []);

  const contextValue = useMemo<AuthContextInterface>(
    () => ({
      user,
      loading,
      error,
      isAuthenticated,
      getTokens: acquireAccessToken,
      loginWithEmailAndPassword: login,
      loginWithCustomToken: login,
      loginWithGoogle: login,
      register: login,
      logout,
      refreshToken: async () => {
        await acquireAccessToken();
      },
      verifyEmail: async () => {
        unsupportedAction('Email verification is handled via Microsoft Entra.');
      },
      handleVerifyEmail,
      resetPassword,
      setNewPassword,
      emailVerified: true,
      authHook: undefined,
    }),
    [
      acquireAccessToken,
      handleVerifyEmail,
      isAuthenticated,
      loading,
      login,
      logout,
      resetPassword,
      setNewPassword,
      user,
      error,
    ],
  );

  return (
    <AzureAuthContext.Provider value={contextValue}>
      {children}
    </AzureAuthContext.Provider>
  );
};

export const refreshMsalToken = async () => {
  try {
    const result = await msalInstance.acquireTokenSilent(loginRequest);
    useUserAuthStore.getState().setAccessToken(result.accessToken);
    return { accessToken: result.accessToken, refreshToken: null };
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const result = await msalInstance.acquireTokenPopup(loginRequest);
      useUserAuthStore.getState().setAccessToken(result.accessToken);
      return { accessToken: result.accessToken, refreshToken: null };
    }
    throw err;
  }
};

export const logoutMsal = async () => {
  await msalInstance.logoutRedirect({
    postLogoutRedirectUri: `${env.VITE_APP_BASE_URL}/auth/login`,
  });
};
